import { z } from "zod";

/**
 * «سودوکو» — a single-player game played on the phone. The server keeps three things for it, so they
 * survive a new phone: the player's unfinished classic game, today's «سودوکوی روز», and the best time
 * at each size and level — and one stats row per finished game (`SudokuResult`: won or lost, mode,
 * mistakes, hints, «assisted»). No match history, no leaderboard, no invite — and Sudoku is
 * deliberately NOT in `GAMES` (the list every table, stat and leaderboard reads); presence counts it
 * under its own key (`SUDOKU_PRESENCE_GAME`).
 *
 * The rules, the puzzle bank and the shape check of a saved game live in `@bing/engine-sudoku`; these
 * are the wire shapes only. The api re-checks every saved state against the stored puzzle with the
 * engine's `validateState` — the zod bounds below only cap a request's size.
 *
 *   GET  /me/sudoku          → {@link SudokuOverview}: both slots and the records (a resume: no gate)
 *   POST /me/sudoku/new      {@link NewSudokuGameInput}  → {@link SudokuGameResponse}  (a NEW game: gated)
 *   POST /me/sudoku/daily    {@link StartSudokuDailyInput} → {@link SudokuGameResponse}  (a NEW game: gated)
 *   PUT  /me/sudoku/save     {@link SaveSudokuInput}     → {@link SudokuGameResponse}  (no gate)
 *   POST /me/sudoku/finish   {@link FinishSudokuInput}   → {@link SudokuFinishResponse} (no gate)
 *
 * A write that names a stale `rev` (another phone moved the game on) is a 409 `CONFLICT` whose
 * `details` are {@link SudokuConflictDetails}: the game as the server has it, which the phone adopts.
 */

export const sudokuSizeSchema = z.union([z.literal(6), z.literal(9)]);
export const sudokuLevelSchema = z.enum(["easy", "medium", "hard"]);

/** The two saved games a player has: one classic game, and today's daily. */
export const SUDOKU_SLOTS = ["classic", "daily"] as const;
export type SudokuSlot = (typeof SUDOKU_SLOTS)[number];
export const sudokuSlotSchema = z.enum(SUDOKU_SLOTS);

/** The record keys, `"<size>-<level>"`. */
export const SUDOKU_RECORD_KEYS = [
  "6-easy",
  "6-medium",
  "6-hard",
  "9-easy",
  "9-medium",
  "9-hard",
] as const;
export type SudokuRecordKey = (typeof SUDOKU_RECORD_KEYS)[number];

/** The longest play time a game may carry: a week of play, far beyond any real game. */
export const SUDOKU_MAX_ELAPSED_MS = 7 * 24 * 60 * 60 * 1000;

const cellIndex = z.number().int().min(0).max(80);
const digitMask = z.number().int().min(0).max(511);

/**
 * MISTAKE CHECKING (the operator, 2026-11-04) — how a game treats mistakes, fixed when it starts (the
 * phone's setting «بررسی اشتباه», on by default, applies to the NEXT game): "check" — a wrong digit is
 * red at once and counted, the 3rd ends the game; "free" — nothing tells right from wrong; a full grid
 * with mistakes says how many (once), then «نشانم بده» may mark them (once — the game is «assisted»).
 */
export const sudokuModeSchema = z.enum(["check", "free"]);
export type SudokuMode = z.infer<typeof sudokuModeSchema>;

/** Mistakes a "check" game survives: this many ends it (the engine's `SUDOKU_MAX_MISTAKES`). */
const MAX_MISTAKES = 3;

/**
 * A game in progress as it is saved: the player's digits (`"0"` = none, row by row), the notes per
 * cell (bit `d − 1` = digit `d`), the cells hints filled, the play time, and the undo history
 * (`[cell, entry, notes]` before each move). Bounds here only cap the size; the engine's
 * `validateState` checks it against the puzzle.
 */
export const sudokuSavedStateSchema = z.object({
  entries: z.string().regex(/^[0-9]{36}$|^[0-9]{81}$/),
  notes: z.array(digitMask).max(81),
  hinted: z.array(cellIndex).max(9),
  elapsedMs: z.number().int().min(0).max(SUDOKU_MAX_ELAPSED_MS),
  undo: z.array(z.tuple([cellIndex, z.number().int().min(0).max(9), digitMask])).max(200),
  // Mistake checking. Defaults: a game saved before it existed reads as a "check" game, clean.
  mode: sudokuModeSchema.default("check"),
  mistakes: z.number().int().min(0).max(MAX_MISTAKES).default(0),
  /** "free": the full-grid message was shown (once per game). */
  fullShown: z.boolean().default(false),
  /** "free": «نشانم بده» was used (once per game). */
  revealed: z.boolean().default(false),
  /** "free": the cells «نشانم بده» marked red that have not changed since. */
  marked: z.array(cellIndex).max(81).default([]),
});
export type SudokuSavedState = z.infer<typeof sudokuSavedStateSchema>;

/** One saved game as the server sends it. `solution` goes to the phone: red marks and hints need no
 *  request. `finishedMs` is set once it was solved (the daily card shows ✓ and that time). */
export const sudokuGameSchema = z.object({
  slot: sudokuSlotSchema,
  size: sudokuSizeSchema,
  level: sudokuLevelSchema,
  /** The daily's Tehran day (`"YYYY-MM-DD"`); null for a classic game. */
  dayKey: z.string().nullable(),
  puzzle: z.string(),
  solution: z.string(),
  state: sudokuSavedStateSchema,
  /** Bumped by every write; a save or finish must name the one it last saw. */
  rev: z.number().int().min(1),
  finishedMs: z.number().int().min(0).nullable(),
  /** How it ended: null while it is on — "lost" is a "check" game's 3rd mistake (a lost daily is
   *  over for that day). */
  outcome: z.enum(["won", "lost"]).nullable().optional(),
});
export type SudokuGame = z.infer<typeof sudokuGameSchema>;

/**
 * A server game with mistake checking's fields filled in where they are missing — an older api's, or
 * a game saved before them reads as a clean "check" game (the schema's defaults, which the web never
 * applies: it reads responses without zod). Anything that is not a game comes back as it is.
 */
export function withSudokuDefaults(game: SudokuGame): SudokuGame {
  if (typeof game !== "object" || game === null) return game;
  const s = game.state as Partial<SudokuSavedState> | undefined;
  if (typeof s !== "object" || s === null) return game;
  return {
    ...game,
    state: {
      ...game.state,
      mode: s.mode ?? "check",
      mistakes: s.mistakes ?? 0,
      fullShown: s.fullShown ?? false,
      revealed: s.revealed ?? false,
      marked: s.marked ?? [],
    },
  };
}

/** The best time (ms) at each size and level; null where the player has not finished one. */
export const sudokuRecordsSchema = z.object({
  "6-easy": z.number().int().min(0).nullable(),
  "6-medium": z.number().int().min(0).nullable(),
  "6-hard": z.number().int().min(0).nullable(),
  "9-easy": z.number().int().min(0).nullable(),
  "9-medium": z.number().int().min(0).nullable(),
  "9-hard": z.number().int().min(0).nullable(),
}) satisfies z.ZodType<Record<SudokuRecordKey, number | null>>;
export type SudokuRecords = z.infer<typeof sudokuRecordsSchema>;

/** `GET /me/sudoku` — today's day key, the unfinished classic game (if any), today's daily (if
 *  started — finished or not), and the records. */
export const sudokuOverviewSchema = z.object({
  today: z.string(),
  classic: sudokuGameSchema.nullable(),
  daily: sudokuGameSchema.nullable(),
  records: sudokuRecordsSchema,
});
export type SudokuOverview = z.infer<typeof sudokuOverviewSchema>;

/** `POST /me/sudoku/new` — a new classic game. `replaces` is the `rev` of the unfinished game the
 *  phone asked to replace (null: it knew of none); any other unfinished game is a 409. */
export const newSudokuGameSchema = z.object({
  size: sudokuSizeSchema,
  level: sudokuLevelSchema,
  replaces: z.number().int().min(1).nullable(),
  /** The phone's setting as the game starts (absent: "check", the default). */
  mode: sudokuModeSchema.optional(),
});
export type NewSudokuGameInput = z.infer<typeof newSudokuGameSchema>;

/** `POST /me/sudoku/daily` — the phone's setting, taken when today's daily starts (absent: "check"). */
export const startSudokuDailySchema = z.object({ mode: sudokuModeSchema.optional() });
export type StartSudokuDailyInput = z.infer<typeof startSudokuDailySchema>;

/** `PUT /me/sudoku/save` — the state of the game in `slot`, written only if `rev` is still current. */
export const saveSudokuSchema = z.object({
  slot: sudokuSlotSchema,
  rev: z.number().int().min(1),
  state: sudokuSavedStateSchema,
});
export type SaveSudokuInput = z.infer<typeof saveSudokuSchema>;

/**
 * `POST /me/sudoku/finish` — the end of a game: the grid (givens and entries, row by row; `"0"` =
 * empty), the play time, and how it went. "won" (the default): the grid must be the solution, and the
 * time sets the record if it beats it — an «assisted» win too. "lost": a "check" game's 3rd mistake —
 * no record; the game is closed (a lost daily is over for the day). Either way one stats row.
 */
export const finishSudokuSchema = z.object({
  slot: sudokuSlotSchema,
  rev: z.number().int().min(1),
  grid: z.string().regex(/^[0-9]{36}$|^[0-9]{81}$/),
  elapsedMs: z.number().int().min(0).max(SUDOKU_MAX_ELAPSED_MS),
  outcome: z.enum(["won", "lost"]).optional(),
  /** The game's mode (must be the stored one). */
  mode: sudokuModeSchema.optional(),
  mistakes: z.number().int().min(0).max(MAX_MISTAKES).optional(),
  hintsUsed: z.number().int().min(0).max(9).optional(),
  /** «نشانم بده» was used. */
  assisted: z.boolean().optional(),
});
export type FinishSudokuInput = z.infer<typeof finishSudokuSchema>;

export const sudokuGameResponseSchema = z.object({ game: sudokuGameSchema });
export type SudokuGameResponse = z.infer<typeof sudokuGameResponseSchema>;

/** `POST /me/sudoku/finish` — the finished game, the records now, and whether this time set one. */
export const sudokuFinishResponseSchema = z.object({
  game: sudokuGameSchema,
  records: sudokuRecordsSchema,
  improved: z.boolean(),
});
export type SudokuFinishResponse = z.infer<typeof sudokuFinishResponseSchema>;

/** The `details` of a 409 `CONFLICT` from a sudoku write: the slot's game as the server has it now
 *  (null: no such game any more — finished, or replaced by a new one). */
export interface SudokuConflictDetails {
  game: SudokuGame | null;
}

/** Why `POST /me/sudoku/finish` refused (400 `VALIDATION`, `details.reason`). Nothing changed. */
export type SudokuFinishRefusal =
  | "WRONG_GRID"
  | "IMPOSSIBLE_TIME"
  /** The finish names another mode than the game's. */
  | "WRONG_MODE"
  /** A "check" game with 3 mistakes cannot be won; a "lost" needs one. */
  | "WRONG_OUTCOME";
